'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart, useCommerceSource, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  NEW_PASSWORD_MIN_LENGTH,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  noticeStyle,
  titleStyle,
  useAccountErrorText,
} from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Choose a new password with the code from the forgotten-password email.
 *
 * <p>The email carries the code twice: in the link, as `?token=`, and written
 * out for a shopper who reads it on another device. The link fills the code in;
 * either way the shopper gives the address the email went to, because a code
 * belongs to one account and the base checks the two together.
 *
 * <p>The token is taken out of the address bar as soon as it is read. Left
 * there it would sit in the browser history, and go out in the `Referer` of
 * the first link the shopper clicks on this page — handing a live credential to
 * whatever site that link points at.
 *
 * <p>A successful reset signs out every session the account had, including
 * this browser's, so the page ends at "sign in with your new password" rather
 * than pretending the shopper is still signed in.
 */
export interface ResetPasswordFormProps {
  title?: Localized;
  intro?: Localized;
  signInHref?: string;
  forgotHref?: string;
}

export function ResetPasswordForm({
  title,
  intro,
  signInHref = '/sign-in',
  forgotHref = '/forgot-password',
}: ResetPasswordFormProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const found = url.searchParams.get('token');
    if (found) {
      setCode(found);
      url.searchParams.delete('token');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  if (!commerce?.resetPassword) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="ResetPasswordForm"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.resetPassword || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.resetPassword({ email: email.trim(), code: code.trim(), password });
      setDone(true);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.resetTitle')}
        </h1>
        {done ? (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.account.resetDone')}
            </p>
            <div style={linkRowStyle}>
              <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
            </div>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.account.resetIntro')}
            </p>
            <form onSubmit={(event) => void submit(event)} style={formStyle}>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              <AccountField
                id="reset-email"
                label={t('commerce.account.email')}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                required
              />
              <AccountField
                id="reset-code"
                label={t('commerce.account.emailedCode')}
                value={code}
                onChange={setCode}
                autoComplete="one-time-code"
                required
                maxLength={64}
              />
              <AccountField
                id="reset-password"
                label={t('commerce.account.newPassword')}
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="new-password"
                required
                minLength={NEW_PASSWORD_MIN_LENGTH}
                hint={t('commerce.account.newPasswordHint', { count: NEW_PASSWORD_MIN_LENGTH })}
              />
              <button type="submit" disabled={busy} style={buttonStyle}>
                {busy ? t('commerce.account.saving') : t('commerce.account.setPassword')}
              </button>
            </form>
            {error && (
              <div style={linkRowStyle}>
                <PortalLink href={forgotHref}>{t('commerce.account.requestNewLink')}</PortalLink>
              </div>
            )}
          </>
        )}
      </div>
    </BlockRoot>
  );
}
